// src/App.js
//
// Chunk 9a — cleaned up.
// • Removed Onboarding component (the language-picker modal). New-user
//   guidance now lives in WelcomeStrip on This Month, gated by
//   profile.welcome_dismissed_at.
// • Kept the MonthlySummary "first-of-the-month" prompt — it's a
//   different flow than welcome.
// • Old route redirects (/budget, /goals, /debts, /transactions, /bills)
//   preserved so anyone with a bookmark still lands somewhere sensible.
//
import React, { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { supabase } from './lib/supabase'
import Login from './components/auth/Login'
import SignUp from './components/auth/SignUp'
import ForgotPassword from './components/auth/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import AppShell from './components/layout/AppShell'
import MonthlySummary from './components/MonthlySummary'
import ThisMonth from './pages/ThisMonth'
import Plan from './pages/Plan'
import Log from './pages/Log'
import NetWorth from './pages/NetWorth'
import MoneyMovement from './pages/MoneyMovement'
import Debts from './pages/Debts'
import Checkins from './pages/Checkins'
import CheckinStart from './pages/CheckinStart'
import CheckinAnswer from './pages/CheckinAnswer'
import CheckinReview from './pages/CheckinReview'
import Settings from './pages/Settings'
import Reports from './pages/Reports'
import Help from './pages/Help'
import Landing from './pages/Landing'
import { CurrencyProvider } from './lib/CurrencyContext'
import { Analytics } from '@vercel/analytics/react'
import './App.css'

// Wrapper that can read location inside Router context — lets the
// invite signup link work even when an existing session is active.
function SignUpRoute({ session }) {
  const location = useLocation()
  const hasInvite = new URLSearchParams(location.search).get('invite')
  if (!session || hasInvite) return <SignUp />
  return <Navigate to="/" />
}

function App() {
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showMonthlySummary, setShowMonthlySummary] = useState(false)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      if (session) checkMonthlySummary(session.user.id)
      setLoading(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      if (session) checkMonthlySummary(session.user.id)
    })
    return () => subscription.unsubscribe()
  }, [])

  // First-of-the-month summary card. Independent of welcome flow —
  // returning users see this on the 1st-5th of every month.
  const checkMonthlySummary = (userId) => {
    const now = new Date()
    const dayOfMonth = now.getDate()
    if (dayOfMonth > 5) return
    const key = `monthlySummary_${userId}_${now.getFullYear()}_${now.getMonth()}`
    const alreadySeen = localStorage.getItem(key)
    if (!alreadySeen) setShowMonthlySummary(true)
  }

  const handleMonthlySummaryDismiss = () => {
    const now = new Date()
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        const key = `monthlySummary_${user.id}_${now.getFullYear()}_${now.getMonth()}`
        localStorage.setItem(key, 'seen')
      }
    })
    setShowMonthlySummary(false)
  }

  if (loading) return (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      height: '100vh', background: '#FAF7F2',
      fontFamily: "'Plus Jakarta Sans', -apple-system, system-ui, sans-serif",
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
        <span style={{
          color: '#1E1040', fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontWeight: 700, fontSize: '26px', letterSpacing: '-0.01em',
        }}>Merq</span>
        <span style={{
          color: '#16A34A', fontFamily: "'Fraunces', serif",
          fontWeight: 700, fontStyle: 'italic',
          fontSize: '26px', letterSpacing: '-0.01em',
        }}>Vault</span>
      </div>
    </div>
  )

  return (
    <CurrencyProvider>
      <Analytics />
      <Router>
        {session && showMonthlySummary && (
          <MonthlySummary onDismiss={handleMonthlySummaryDismiss} />
        )}
        <Routes>
        <Route path="/landing" element={<Landing session={session} />} />

        <Route path="/" element={
          session ? <AppShell /> : <Landing session={null} />
        }>
          <Route index element={session ? <ThisMonth /> : null} />

          {/* New IA: 5-item sidebar */}
          <Route path="plan" element={<Plan />} />
          <Route path="log" element={<Log />} />
          <Route path="networth" element={<NetWorth />} />
          <Route path="money-movement" element={<MoneyMovement />} />
          <Route path="report" element={<Reports />} />

          {/* Profile menu */}
          <Route path="settings" element={<Settings />} />
          <Route path="help" element={<Help />} />

          {/* Old paths → new IA (kept for bookmarks) */}
          <Route path="budget"       element={<Navigate to="/plan" replace />} />
          <Route path="goals"        element={<Navigate to="/plan" replace />} />
          <Route path="debts" element={<Debts />} />
          <Route path="checkins" element={<Checkins />} />
          <Route path="checkins/:id/start" element={<CheckinStart />} />
          <Route path="checkins/:id/answer" element={<CheckinAnswer />} />
          <Route path="checkins/:id/review" element={<CheckinReview />} />
          <Route path="transactions" element={<Navigate to="/log" replace />} />
          <Route path="bills"        element={<Navigate to="/log" replace />} />
          <Route path="dashboard"    element={<Navigate to="/" replace />} />
        </Route>

        <Route path="/login" element={!session ? <Login /> : <Navigate to="/" />} />
        <Route path="/signup" element={<SignUpRoute session={session} />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
      </Routes>
      </Router>
    </CurrencyProvider>
  )
}

export default App
